import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
// material
import { alpha, styled } from '@mui/material/styles';
import { Box, Link, Stack, Avatar, Drawer, Tooltip, Typography, CardActionArea, Button } from '@mui/material';
// hooks
import useAuth from 'hooks/useAuth';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
// components
import Logo from 'components/Logo';
import Scrollbar from 'components/Scrollbar';
import NavSection from 'components/NavSection';
import MyAvatar from 'components/MyAvatar';
import { useDispatch } from 'react-redux';
//
import { MHidden } from 'components/@material-extend';
import { useSnackbar } from 'notistack';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { createPartnerUsersApi } from 'redux/analytics/analyticsApi';
import sidebarConfig from './SidebarConfig';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 240;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12]
}));

// ----------------------------------------------------------------------

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <Tooltip title="Mini Menu">
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: 'text.primary',
          justifyContent: 'center',
          border: 'solid 1px currentColor',
          ...(collapseClick && {
            borderWidth: 2
          })
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            transition: (theme) => theme.transitions.create('all'),
            ...(collapseClick && {
              width: 0,
              height: 0
            })
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ user, logout, isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const [logoutDialog, setlogoutDialog] = useState(false);
  const dispatch = useDispatch();
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const handleLogout = async () => {
    setlogoutDialog(true);
  };

  const closeLogoutDialog = () => {
    setlogoutDialog(false);
  };

  const storeEndTime = async () => {
    try {
      const endTime = new Date();
      await localStorage.setItem('AppEndTime', endTime.toISOString()); // Serialize Date to ISO string

      // Retrieve start time and end time from localStorage
      const startingTimeStr = await localStorage.getItem('AppStartTime');
      const endingTimeStr = await localStorage.getItem('AppEndTime');

      if (startingTimeStr && endingTimeStr) {
        const startingTime = new Date(startingTimeStr); // Deserialize ISO string to Date
        const endingTime = new Date(endingTimeStr); // Deserialize ISO string to Date

        console.log('Starting Time:', startingTime);
        console.log('Ending Time:', endingTime);

        const timeDifferenceMs = endingTime - startingTime;
        console.log('Time difference in milliseconds:', timeDifferenceMs);
        await dispatch(
          createPartnerUsersApi({
            module: 'SCREEN_MODE',
            event: 'OFFLINE',
            message: 'Screen Monitor',
            startTime: startingTime,
            moduleInformation: user?.userId,
            endTime: endingTime,
            totalTimeDuration: timeDifferenceMs,
            platform: 'ADMIN_PAGE',
            phoneNumber: user?.contactInfo?.phoneNumber?.primary || ''
          })
        );

        await logout();
        navigate('/');
        setTimeout(() => {
          window.location.reload(false);
        }, 500);
      }

      console.log('App end time stored:', endTime);
    } catch (error) {
      console.error('Error saving end time:', error);
    }
  };

  const onLogoutAgree = async () => {
    try {
      if (user?.role === 'OEM') {
        storeEndTime();
      }
      if (user?.role !== 'OEM') {
        await logout();
        navigate('/');
        setTimeout(() => {
          window.location.reload(false);
        }, 500);
      }
    } catch (error) {
      console.error(error, 'wfkjnje..');
      enqueueSnackbar('Unable to logout', { variant: 'error' });
    }
  };

  const renderContent = (
    <>
      <Scrollbar
        sx={{
          height: '100%',
          '& .simplebar-content': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Stack
          spacing={3}
          sx={{
            px: 2.5,
            // pt: 3,
            // pb: 2,
            ...(isCollapse && {
              alignItems: 'center'
            })
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ pt: user?.role !== 'OEM' ? 0 : '4px' }}
          >
            <Box component={RouterLink} to="/" sx={{ display: 'inline-flex', width: '100%' }}>
              {(user?.role === 'ADMIN' || (user?.role === 'EMPLOYEE' && user?.oemId === 'SERVICEPLUG')) && <Logo />}
              {user?.role === 'OEM' && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ height: '70px', width: '90%' }}
                >
                  <Box
                    component="img"
                    src={user?.documentImageList?.logo?.docURL}
                    sx={{ width: '100%', height: '55px', borderRadius: '6px' }}
                  />
                </Stack>
              )}
              {user?.role === 'EMPLOYEE' && user?.oemId !== 'SERVICEPLUG' && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ height: '70px', width: '90%' }}
                >
                  <Box
                    component="img"
                    src={user?.employeeCompanyDetails[0]?.documentImageList?.logo?.docURL}
                    sx={{ width: '100%', height: '55px', borderRadius: '6px' }}
                  />
                </Stack>
              )}
            </Box>

            <MHidden width="lgDown">
              {!isCollapse && <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />}
            </MHidden>
          </Stack>
        </Stack>

        <NavSection navConfig={sidebarConfig} isShow={!isCollapse} user={user} />
      </Scrollbar>
      <ConfirmationDialog
        open={logoutDialog}
        handleClose={closeLogoutDialog}
        onAgree={onLogoutAgree}
        dialogContent="Are you sure you want to log out?"
        titleContent="Logout Confirmation"
        agreeText="Yes"
        disagreeText="No"
      />
      <Box sx={{ p: 2, pt: 1, width: '100%' }}>
        <Button fullWidth color="inherit" variant="outlined" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
    </>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
